import { useReducer, type KeyboardEvent } from "react";
import { PatternFormat } from "react-number-format";

import { LINK_BUTTON_CLASS } from "components/FlowButtons";
import { CONTINUE_BUTTON_CLASS } from "components/FormV2";
import { inputClass } from "components/FormV2/fields/chrome";

import { useTrackLeadFlowEvent } from "pages/StartLead/analytics";
import { leadFlowEvents, type ConfirmSurface } from "pages/StartLead/events";

import {
  destinationEditReducer,
  prefillFor,
  saveDecision,
  saveEventOf,
  VIEWING,
  type CorrectionResult,
  type DestinationChannel,
  type DestinationEdit,
} from "./destination-edit";
import { CORRECTION_CLOSED_MESSAGE } from "./correction-errors";
import type { CorrectionProof } from "./login-grant-flow";

const FIELD_LABEL: Record<DestinationChannel, string> = {
  phone: "Phone number",
  email: "Email address",
};

const ZIP_LABEL = "ZIP code from your form";

const FIELD_ID = "destination-correction";
const ERROR_ID = "destination-correction-error";

function enterKey(onEnter: () => void) {
  return (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    onEnter();
  };
}

function DestinationField({
  channel,
  draft,
  invalid,
  disabled,
  autoFocus,
  onChange,
  onEnter,
}: {
  channel: DestinationChannel;
  draft: string;
  invalid: boolean;
  disabled: boolean;
  autoFocus: boolean;
  onChange: (value: string) => void;
  onEnter: () => void;
}) {
  const common = {
    id: FIELD_ID,
    name: FIELD_ID,
    "aria-label": FIELD_LABEL[channel],
    "aria-invalid": invalid || undefined,
    "aria-describedby": invalid ? ERROR_ID : undefined,
    className: `${inputClass(invalid)} h-12 w-full min-w-0 flex-1`,
    disabled,
    autoFocus,
    onKeyDown: enterKey(onEnter),
  };
  if (channel === "phone") {
    return (
      <PatternFormat
        {...common}
        type="tel"
        format="(###) ###-####"
        placeholder="(555) 555-5555"
        autoComplete="tel-national"
        inputMode="tel"
        value={draft}
        onValueChange={(values, source) => {
          if (source.source === "event") onChange(values.formattedValue);
        }}
      />
    );
  }
  return (
    <input
      {...common}
      type="email"
      placeholder="you@example.com"
      autoComplete="email"
      inputMode="email"
      value={draft}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

function ZipField({
  value,
  invalid,
  disabled,
  onChange,
  onEnter,
}: {
  value: string;
  invalid: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
  onEnter: () => void;
}) {
  return (
    <input
      type="text"
      name="destination-correction-zip"
      aria-label={ZIP_LABEL}
      placeholder={ZIP_LABEL}
      inputMode="numeric"
      maxLength={5}
      autoComplete="postal-code"
      className={`${inputClass(invalid)} h-12 w-full min-w-0 flex-1 placeholder:text-sm`}
      disabled={disabled}
      value={value}
      onChange={(event) => onChange(event.target.value.replace(/\D/g, ""))}
      onKeyDown={enterKey(onEnter)}
    />
  );
}

/**
 * The "Not right? Fix it" row under the number line. Closed it is that one
 * button; open it is the editor in the same place, with the number line still
 * above it. With the signed link as proof the editor holds the destination
 * shown, to fix; with only the ZIP code from the form as proof it starts
 * empty and asks for the ZIP code too. A destination that can no longer be
 * changed here is told in this row, and the fix is not offered again.
 */
export default function FixRow({
  surface,
  channel,
  display,
  proof,
  onSave,
}: {
  /** The screen hosting the row, reported with each of its events. */
  surface: ConfirmSurface;
  channel: DestinationChannel;
  /** The destination as the number line shows it. */
  display: string;
  proof: NonNullable<CorrectionProof>;
  onSave: (value: string, zip: string | null) => Promise<CorrectionResult>;
}) {
  const [edit, dispatch] = useReducer(destinationEditReducer, VIEWING);
  const trackEvent = useTrackLeadFlowEvent();
  const fix = { surface, channel, proof };

  const update = async () => {
    if (edit.kind === "viewing" || edit.saving) return;
    const decision = saveDecision(channel, display, edit, proof);
    if (decision.kind === "unchanged") {
      dispatch({ type: "cancel" });
      return;
    }
    if (decision.kind === "invalid") {
      trackEvent(saveEventOf(fix, decision));
      dispatch({ type: "save_failed", message: decision.message });
      return;
    }
    dispatch({ type: "save_started" });
    const result = await onSave(decision.value, decision.zip);
    trackEvent(saveEventOf(fix, result));
    if (result.kind === "done") dispatch({ type: "saved" });
    if (result.kind === "closed") dispatch({ type: "save_closed" });
    if (result.kind === "stay") {
      dispatch({ type: "save_failed", message: result.message });
    }
  };

  return (
    <div
      data-slot="fix"
      className="mt-1 flex min-h-5 flex-col text-sm leading-5"
    >
      <FixRowContent
        edit={edit}
        channel={channel}
        proof={proof}
        onOpen={() => {
          trackEvent(leadFlowEvents.fixOpened(fix));
          dispatch({
            type: "open",
            draft: prefillFor(channel, display, proof),
          });
        }}
        onCancel={() => dispatch({ type: "cancel" })}
        onDraft={(draft) => dispatch({ type: "change", draft })}
        onZip={(zip) => dispatch({ type: "change_zip", zip })}
        onUpdate={() => void update()}
      />
    </div>
  );
}

function FixRowContent({
  edit,
  channel,
  proof,
  onOpen,
  onCancel,
  onDraft,
  onZip,
  onUpdate,
}: {
  edit: DestinationEdit;
  channel: DestinationChannel;
  proof: NonNullable<CorrectionProof>;
  onOpen: () => void;
  onCancel: () => void;
  onDraft: (draft: string) => void;
  onZip: (zip: string) => void;
  onUpdate: () => void;
}) {
  if (edit.kind === "viewing") {
    if (edit.closed) {
      return (
        <p role="alert" className="text-red-800">
          {CORRECTION_CLOSED_MESSAGE}
        </p>
      );
    }
    return (
      <button
        type="button"
        className={`${LINK_BUTTON_CLASS} self-start`}
        onClick={onOpen}
      >
        Not right? Fix it
      </button>
    );
  }
  const invalid = edit.error !== null;
  const updateButton = (
    <button
      type="button"
      className={`${CONTINUE_BUTTON_CLASS} h-12 shrink-0 !px-4 2xs:!px-[26px]`}
      onClick={onUpdate}
      disabled={edit.saving}
      aria-busy={edit.saving || undefined}
    >
      {edit.saving ? "Updating..." : "Update"}
    </button>
  );
  const destinationField = (
    <DestinationField
      channel={channel}
      draft={edit.draft}
      invalid={invalid}
      disabled={edit.saving}
      autoFocus
      onChange={onDraft}
      onEnter={onUpdate}
    />
  );
  return (
    <div className="space-y-2 pt-1">
      {proof === "zip" ? (
        <>
          <div className="flex">{destinationField}</div>
          <div className="flex items-center gap-2">
            <ZipField
              value={edit.zip}
              invalid={invalid}
              disabled={edit.saving}
              onChange={onZip}
              onEnter={onUpdate}
            />
            {updateButton}
          </div>
        </>
      ) : (
        <div className="flex items-center gap-2">
          {destinationField}
          {updateButton}
        </div>
      )}
      <div>
        <button
          type="button"
          className={`${LINK_BUTTON_CLASS} disabled:opacity-50`}
          onClick={onCancel}
          disabled={edit.saving}
        >
          Cancel
        </button>
      </div>
      {edit.error !== null && (
        <p id={ERROR_ID} role="alert" className="text-red-800">
          {edit.error}
        </p>
      )}
    </div>
  );
}
